import {
    getAnswerPrompt,
    type StudentResultAnswer,
    type StudentResultExercise,
} from "../../../../helpers/StudentResultModel";
import { getIsScoreShown } from "../../../../helpers/StudentResultScoreReveal";
import useBreakpoints from "../../../../hooks/useBreakpoints";
import StudentResultAnswerBox from "../StudentResultAnswerBox";
import StudentResultAnswerCheck from "../StudentResultAnswerCheck";
import StudentResultAnswerRow from "../StudentResultAnswerRow";
import StudentResultAnswerScore from "../StudentResultAnswerScore";
import StudentResultTeacherComment, { getHasTeacherComment } from "../StudentResultTeacherComment";
import { getTeacherCommentTone } from "../../../../constants/StudentResultCommentTones";

interface StudentResultAnswersStandardProps {
    exercise: StudentResultExercise;
}

/**
 * Table exercise, and the fallback for an unknown type: prompt on one side, the student's answer on
 * the other, in the direction each word was asked. Desktop keeps the table's two columns; mobile
 * stacks each row into a card.
 */
const StudentResultAnswersStandard = (props: StudentResultAnswersStandardProps) => {
    const { isAboveMD } = useBreakpoints();
    const isScoreShown = getIsScoreShown(props.exercise.isScored);

    const renderPrompt = (answer: StudentResultAnswer) => {
        return <p className="text-l text-p6-grey-dark px-xs break-words">{getAnswerPrompt(answer) || "—"}</p>;
    };

    const renderAnswer = (answer: StudentResultAnswer) => {
        return (
            <div className="flex items-center gap-s w-full">
                <StudentResultAnswerBox
                    state={answer.state}
                    text={answer.studentAnswer}
                />
                <StudentResultAnswerCheck state={answer.state} />
            </div>
        );
    };

    /** Prompt and answer in the order the student saw them. */
    const renderCells = (answer: StudentResultAnswer): [JSX.Element, JSX.Element] => {
        return answer.isQuestionShown
            ? [renderPrompt(answer), renderAnswer(answer)]
            : [renderAnswer(answer), renderPrompt(answer)];
    };

    // The score column keeps a fixed width so every row's divider lines up regardless of its content.
    const renderScoreColumn = (answer: StudentResultAnswer) => {
        if (!isScoreShown) return null;
        return (
            <div className="flex items-start shrink-0 w-[100px] py-m">
                <StudentResultAnswerScore
                    answer={answer}
                    isScored={props.exercise.isScored}
                />
            </div>
        );
    };

    const renderDesktopRow = (answer: StudentResultAnswer) => {
        const [first, second] = renderCells(answer);

        return (
            <div className="grid grid-cols-2">
                <div className="flex items-center w-full p-m border-r border-p6-grey-light">{first}</div>
                <div className="flex items-center w-full p-m">{second}</div>
            </div>
        );
    };

    // Inside the card, so the bubble stays within the border that groups the row on mobile.
    const renderMobileComment = (answer: StudentResultAnswer) => {
        if (!getHasTeacherComment(answer.teacherComment)) return null;
        return (
            <div className="pt-s px-xs">
                <StudentResultTeacherComment
                    comment={answer.teacherComment}
                    tone={getTeacherCommentTone(answer.state)}
                />
            </div>
        );
    };

    const renderMobileCard = (answer: StudentResultAnswer) => {
        const [first, second] = renderCells(answer);

        return (
            <div className="flex flex-col gap-s bg-white border border-p6-grey-light rounded-medium p-m my-xs">
                <div className="flex justify-end">
                    <StudentResultAnswerScore
                        answer={answer}
                        isScored={props.exercise.isScored}
                    />
                </div>
                {first}
                {second}
                {renderMobileComment(answer)}
            </div>
        );
    };

    return (
        <ul
            className="flex flex-col"
            data-testid="student-result-answers-standard"
        >
            {props.exercise.answers.map((answer, index) => (
                <StudentResultAnswerRow
                    key={answer.id}
                    isLast={index === props.exercise.answers.length - 1}
                    hasPadding={false}
                    hasSeparator={isAboveMD}
                    comment={isAboveMD ? answer.teacherComment : undefined}
                    tone={isAboveMD ? getTeacherCommentTone(answer.state) : undefined}
                    score={isAboveMD ? renderScoreColumn(answer) : undefined}
                >
                    {isAboveMD ? renderDesktopRow(answer) : renderMobileCard(answer)}
                </StudentResultAnswerRow>
            ))}
        </ul>
    );
};

export default StudentResultAnswersStandard;
